import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  afterNextRender,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type CalendarView = 'day' | 'month' | 'year';
export type CalendarSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

interface CalendarDay {
  date: Date;
  day: number;
  inCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}

const YEAR_BLOCK_SIZE = 12;

/** Lundi 4 janvier 2021 — référence pour libeller une semaine commençant le lundi. */
const FIRST_MONDAY = new Date(2021, 0, 4);

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CalendarComponent), multi: true },
  ],
})
export class CalendarComponent implements ControlValueAccessor, OnDestroy {

  /** Texte affiché tant qu'aucune date n'est choisie. Vide = masque traduit. */
  readonly placeholder = input<string>('');

  @Input() disabled = false;

  /** Taille du champ déclencheur (le panneau, lui, garde une largeur fixe). */
  @Input() size: CalendarSize = 'small';

  /** Émis uniquement quand l'utilisateur valide via le bouton Confirm (date ISO 'YYYY-MM-DD'). */
  @Output() confirmed = new EventEmitter<string>();

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly translate = inject(TranslateService);

  /** Langue courante de l'application, suivie pour relibeller mois et jours. */
  private readonly lang = signal(this.translate.currentLang || this.translate.defaultLang || 'en');

  readonly isOpen = signal(false);
  readonly view = signal<CalendarView>('day');
  readonly value = signal<string | null>(null);
  readonly cursor = signal<Date>(new Date());
  readonly pendingDate = signal<Date | null>(null);

  /** Date sélectionnée, toujours en jour/mois/année ; vide si aucune sélection. */
  readonly displayLabel = computed(() => {
    const v = this.value();
    if (!v) return '';
    const [y, m, d] = v.split('-');
    return `${d}/${m}/${y}`;
  });

  /**
   * Masque de saisie affiché à défaut de date : l'ordre reste jour/mois/année mais les
   * lettres suivent la langue (JJ/MM/AAAA en français). Un placeholder fourni par le
   * consommateur a priorité ; le template le passe au pipe `translate`, qui rend une
   * chaîne libre telle quelle.
   */
  readonly placeholderLabel = computed(() => this.placeholder() || 'CALENDAR.DATE_PLACEHOLDER');

  /** Noms de mois abrégés dans la langue de l'application. */
  readonly monthNames = computed<string[]>(() => {
    const format = this.dateFormatter({ month: 'short' });
    return Array.from({ length: 12 }, (_, month) => format(new Date(2021, month, 1)));
  });

  /** Initiales des jours, semaine commençant le lundi. */
  readonly weekdayLabels = computed<string[]>(() => {
    const format = this.dateFormatter({ weekday: 'narrow' });
    return Array.from({ length: 7 }, (_, offset) =>
      format(new Date(FIRST_MONDAY.getFullYear(), FIRST_MONDAY.getMonth(), FIRST_MONDAY.getDate() + offset)),
    );
  });

  readonly monthLabel = computed(() => {
    const c = this.cursor();
    return `${this.monthNames()[c.getMonth()]} ${c.getFullYear()}`;
  });

  readonly yearLabel = computed(() => `${this.cursor().getFullYear()}`);

  readonly yearBlockLabel = computed(() => {
    const start = this.blockStartYear();
    return `${start}-${start + YEAR_BLOCK_SIZE - 1}`;
  });

  readonly weeks = computed<CalendarDay[][]>(() => {
    const c = this.cursor();
    const year = c.getFullYear();
    const month = c.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // lundi = 0 ... dimanche = 6
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    const today = new Date();
    const pending = this.pendingDate();

    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      days.push({
        date,
        day: date.getDate(),
        inCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDay(date, today),
        isSelected: !!pending && this.isSameDay(date, pending),
      });
    }

    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < 6; i++) weeks.push(days.slice(i * 7, i * 7 + 7));
    return weeks;
  });

  readonly monthsGrid = computed(() => {
    const selectedMonth = this.pendingDate() ?? this.cursor();
    const year = this.cursor().getFullYear();
    return this.monthNames().map((label, index) => ({
      label,
      index,
      isSelected: selectedMonth.getFullYear() === year && selectedMonth.getMonth() === index,
    }));
  });

  private readonly blockStartYear = computed(() => Math.floor(this.cursor().getFullYear() / YEAR_BLOCK_SIZE) * YEAR_BLOCK_SIZE);

  readonly yearsGrid = computed(() => {
    const start = this.blockStartYear();
    const selectedYear = (this.pendingDate() ?? this.cursor()).getFullYear();
    return Array.from({ length: YEAR_BLOCK_SIZE }, (_, i) => {
      const year = start + i;
      return { year, isSelected: year === selectedYear };
    });
  });

  private readonly onDocClick = (e: MouseEvent) => {
    if (!this.el.nativeElement.contains(e.target as Node)) this.close();
  };

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(event => this.lang.set(event.lang));

    afterNextRender(() => document.addEventListener('click', this.onDocClick, true));
  }

  /**
   * Libellés de dates fournis par la plateforme pour la langue courante : les noms de mois
   * et de jours suivent ainsi la langue de l'application sans table de traduction à tenir.
   * Une langue inconnue de `Intl` retombe sur l'anglais plutôt que de faire échouer le rendu.
   */
  private dateFormatter(options: Intl.DateTimeFormatOptions): (date: Date) => string {
    let formatter: Intl.DateTimeFormat;
    try {
      formatter = new Intl.DateTimeFormat(this.lang(), options);
    } catch {
      formatter = new Intl.DateTimeFormat('en', options);
    }
    return (date: Date) => {
      const label = formatter.format(date);
      return label.charAt(0).toUpperCase() + label.slice(1);
    };
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onDocClick, true);
  }

  // ── CVA ─────────────────────────────────────────────────────────────────────
  private onChange: (v: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: string | null): void {
    this.value.set(v);
  }
  registerOnChange(fn: (v: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled = d; }

  // ── Ouverture / fermeture ─────────────────────────────────────────────────────
  toggle(): void {
    if (this.disabled) return;
    if (this.isOpen()) { this.close(); return; }
    const base = this.value() ? this.parseIso(this.value()!) : new Date();
    this.cursor.set(base);
    this.pendingDate.set(this.value() ? base : null);
    this.view.set('day');
    this.isOpen.set(true);
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.onTouched();
  }

  goToView(view: CalendarView): void {
    this.view.set(view);
  }

  // ── Navigation ────────────────────────────────────────────────────────────────
  prevPeriod(): void { this.shiftPeriod(-1); }
  nextPeriod(): void { this.shiftPeriod(1); }

  private shiftPeriod(dir: number): void {
    const c = this.cursor();
    if (this.view() === 'day') {
      this.cursor.set(new Date(c.getFullYear(), c.getMonth() + dir, 1));
    } else if (this.view() === 'year') {
      this.cursor.set(new Date(c.getFullYear() + dir * YEAR_BLOCK_SIZE, c.getMonth(), 1));
    } else {
      this.cursor.set(new Date(c.getFullYear() + dir, c.getMonth(), 1));
    }
  }

  // ── Sélection ─────────────────────────────────────────────────────────────────
  selectDay(day: CalendarDay): void {
    this.pendingDate.set(day.date);
    if (!day.inCurrentMonth) this.cursor.set(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
  }

  selectMonth(monthIndex: number): void {
    const c = this.cursor();
    this.cursor.set(new Date(c.getFullYear(), monthIndex, 1));
    this.view.set('day');
  }

  selectYear(year: number): void {
    const c = this.cursor();
    this.cursor.set(new Date(year, c.getMonth(), 1));
    this.view.set('month');
  }

  confirm(): void {
    const d = this.pendingDate();
    if (!d) return;
    const iso = this.formatIso(d);
    this.value.set(iso);
    this.onChange(iso);
    this.confirmed.emit(iso);
    this.close();
  }

  get hasPendingSelection(): boolean {
    return this.pendingDate() !== null;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private formatIso(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private parseIso(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }
}
